import { motion } from "framer-motion";
import { IMAGES } from "@/lib/images";
import { Reveal } from "./Reveal";

const CHAPTERS = [
  {
    n: "01",
    title: "Foundations of the Crown",
    desc:
      "Precision sectioning, clean partings, and tension control. Build the muscle memory and hand technique every crowned artist is known for.",
    points: ["Anatomy of a flawless parting", "Tension & scalp health", "Speed drills & consistency"],
    img: IMAGES.curriculum[0],
  },
  {
    n: "02",
    title: "Signature Techniques",
    desc:
      "Master the styles clients pay premium for — knotless box braids, feed-in cornrows, boho, and intricate freestyle artistry.",
    points: ["Knotless & feed-in mastery", "Boho & goddess styling", "Freestyle pattern design"],
    img: IMAGES.curriculum[1],
  },
  {
    n: "03",
    title: "The Business of Braiding",
    desc:
      "Turn talent into a thriving brand. Pricing, bookings, client experience, and building a name that commands respect.",
    points: ["Premium pricing strategy", "Booking & client care systems", "Personal brand building"],
    img: IMAGES.curriculum[2],
  },
  {
    n: "04",
    title: "Certification & Beyond",
    desc:
      "Graduate as a Crowned™ Pro. Receive your certificate, a portfolio review, and lifetime access to the BNB alumni community.",
    points: ["Crowned™ Pro certificate", "Portfolio & photo day", "Lifetime alumni network"],
    img: IMAGES.curriculum[3],
  },
];

export const Curriculum = () => (
  <section id="curriculum" data-testid="curriculum-section" className="bg-wine-deep py-24 sm:py-32">
    <div className="max-w-[1400px] mx-auto px-6">
      <Reveal className="max-w-2xl mb-20">
        <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
          The Curriculum
        </span>
        <h2 className="font-display font-light text-cream text-5xl sm:text-6xl leading-[1] mt-5">
          Four chapters to your <span className="italic text-gold">crown</span>.
        </h2>
        <p className="text-cream/60 mt-6 text-lg leading-relaxed">
          A meticulously sequenced journey from fundamentals to a fully certified, business-ready
          braiding professional.
        </p>
      </Reveal>

      <div className="space-y-6">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.n} delay={i * 0.05} testId={`chapter-${c.n}`}>
            <div className="group grid lg:grid-cols-12 gap-6 items-center rounded-3xl border border-gold/15 bg-wine-surface/40 p-6 sm:p-8 hover:border-gold/40 transition-colors duration-500">
              <div className="lg:col-span-1">
                <span className="font-display text-6xl text-gold/50 group-hover:text-gold transition-colors duration-500">
                  {c.n}
                </span>
              </div>
              <div className="lg:col-span-6">
                <h3 className="font-display text-3xl sm:text-4xl text-cream">{c.title}</h3>
                <p className="text-cream/60 mt-3 leading-relaxed max-w-xl">{c.desc}</p>
                <div className="flex flex-wrap gap-2 mt-5">
                  {c.points.map((p) => (
                    <span
                      key={p}
                      className="font-mono text-[10px] uppercase tracking-[0.15em] text-gold border border-gold/25 rounded-full px-3 py-1.5"
                    >
                      {p}
                    </span>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="relative overflow-hidden rounded-2xl aspect-[16/10]">
                  <motion.img
                    src={c.img}
                    alt={c.title}
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
